// src/views/adminView/contractsView/detalle/DetalleResumenTab.jsx
// Tab "Resumen" del detalle de empresa: contacto (principal + facturación),
// equipo asignado (sin backend), usuarios del portal con su perfil y
// condiciones del contrato. Defensivo ante campos ausentes (el contract puede
// venir de la lista). Lo único editable es la ficha (contacto, RUT, razón social
// y dirección fiscal), y solo con el contrato vigente: con uno inactivo
// contracts responde 404.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Mail, Pencil, Phone, User } from "lucide-react";
import { Button } from "../../../../components/ui/Button";
import { Card, CardHeader, CardTitle } from "../../../../components/ui/Card";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { formatCLP, formatDate } from "../../../../utils/formatters";
import UsuariosEmpresaSection from "../compliance/UsuariosEmpresaSection";
import EditCompanyProfileModal from "./EditCompanyProfileModal";

// Ítem de la grilla de contacto: icono en tile 9x9 + label uppercase + valor.
function ContactItem({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 shrink-0 rounded-lg bg-bg-muted flex items-center justify-center text-fg-muted">
        <Icon className="w-4 h-4" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <dt className="text-[11px] uppercase tracking-wider text-fg-muted">{label}</dt>
        <dd className={`mt-0.5 text-sm text-fg truncate ${mono ? "font-mono" : ""}`}>
          {value || "—"}
        </dd>
      </div>
    </div>
  );
}

/**
 * @param {{ contract: object, onGoCuentas?: Function, canEditTaxId?: boolean,
 *           onProfileSaved?: () => (void|Promise<void>) }} props
 *   Sin `onProfileSaved` la ficha queda de solo lectura.
 */
export default function DetalleResumenTab({ contract, onGoCuentas, canEditTaxId = false, onProfileSaved }) {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = useState(false);
  const canEditProfile = Boolean(onProfileSaved) && Boolean(contract?.companyId?._id) && contract?.status !== "inactive";
  const company = contract?.companyId || {};
  const primary = company.contacts?.primary || {};
  const billing = company.contacts?.billing || {};
  const insurance = contract?.insurancePolicy;

  const startDate = contract?.validityPeriod?.startDate ?? contract?.dateSigned;
  const endDate = contract?.validityPeriod?.endDate;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Contacto */}
      <Card className="lg:col-span-2">
        <CardHeader className="flex items-center justify-between gap-2">
          <CardTitle>{t("Contacto")}</CardTitle>
          {canEditProfile && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
              iconLeft={<Pencil className="w-4 h-4" aria-hidden="true" />}
            >
              {t("Editar ficha")}
            </Button>
          )}
        </CardHeader>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ContactItem icon={User} label={t("Contacto principal")} value={primary.name} />
          <ContactItem icon={Mail} label={t("Email")} value={primary.email} />
          <ContactItem icon={Phone} label={t("Teléfono")} value={primary.phone} mono />
          <ContactItem icon={Building2} label={t("RUT")} value={company.businessIdentifier} mono />
        </dl>
        <div className="mt-5 pt-4 border-t border-border">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-muted mb-3">
            {t("Facturación")}
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ContactItem icon={User} label={t("Contacto de facturación")} value={billing.name} />
            <ContactItem icon={Mail} label={t("Email")} value={billing.email} />
            <ContactItem icon={Phone} label={t("Teléfono")} value={billing.phone} mono />
          </dl>
        </div>
      </Card>

      {editOpen && (
        <EditCompanyProfileModal
          open
          onClose={() => setEditOpen(false)}
          contract={contract}
          canEditTaxId={canEditTaxId}
          onSaved={async () => {
            setEditOpen(false);
            await onProfileSaved();
          }}
        />
      )}

      {/* Equipo asignado — el backend no expone asignación KAM/CS, no se inventa. */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>{t("Equipo asignado")}</CardTitle>
        </CardHeader>
        <p className="text-sm text-fg-muted">{t("Sin equipo asignado a esta cuenta.")}</p>
        <PendingBackendNotice className="mt-3">
          {t("Asignación de KAM/CS — en construcción")}
        </PendingBackendNotice>
      </Card>

      {/* Usuarios del portal (con su perfil) — misma sección compartida con la
          pestaña Cumplimiento y el drawer de cartera; datos de useClientAccounts. */}
      <div className="lg:col-span-3">
        <UsuariosEmpresaSection contract={contract} onGoCuentas={onGoCuentas} />
      </div>

      {/* Condiciones del contrato */}
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle>{t("Condiciones del contrato")}</CardTitle>
        </CardHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <div className="bg-bg-muted rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wider text-fg-muted">
              {t("Vigencia desde")}
            </div>
            <div className="mt-1 text-sm font-medium text-fg tabular-nums">
              {startDate ? formatDate(startDate) : "—"}
            </div>
          </div>
          <div className="bg-bg-muted rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wider text-fg-muted">
              {t("Vigencia hasta")}
            </div>
            <div className="mt-1 text-sm font-medium text-fg tabular-nums">
              {endDate ? formatDate(endDate) : t("Indefinida")}
            </div>
          </div>
          <div className="bg-bg-muted rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wider text-fg-muted">
              {t("Hora de retiro")}
            </div>
            <div className="mt-1 text-sm font-medium text-fg tabular-nums">
              {company.pickUpTime || "—"}
            </div>
          </div>
          {insurance && (
            <div className="bg-bg-muted rounded-lg p-3" title={insurance.details || undefined}>
              <div className="text-[11px] uppercase tracking-wider text-fg-muted">
                {t("Seguro")}
              </div>
              <div className="mt-1 text-sm font-medium text-fg tabular-nums">
                {insurance.formulaType === "percentage"
                  ? `${insurance.value}%`
                  : formatCLP(insurance.value)}
              </div>
            </div>
          )}
        </div>
        {contract?.additionalTerms && (
          <p className="mt-4 text-sm text-fg-muted whitespace-pre-line">
            {contract.additionalTerms}
          </p>
        )}
      </Card>
    </div>
  );
}
